import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import logo from '../assets/LogoW.webp';
import AssignmentsView from './AssignmentsView';
import LiveEventView from './LiveEventView';
import HistoryView from './HistoryView';
import ProfileView from './ProfileView';
import EventCompletionModal from './EventCompletionModal';

const API = process.env.REACT_APP_API_URL || 'https://api.pixlybooth.com';

export default function OrganizerLayout() {
  const { user, logout } = useAuth();
  const { socket } = useSocket();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assignments'); // 'assignments' | 'live' | 'history' | 'profile'
  const [assignmentSubTab, setAssignmentSubTab] = useState('requests');
  const [activeLiveEvent, setActiveLiveEvent] = useState(null);
  const [completionModalEvent, setCompletionModalEvent] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/organizer/events?tab=all`, { withCredentials: true });
      const evts = res.data.events || [];
      setEvents(evts);

      // Auto-select current live/accepted event if activeLiveEvent is not set
      if (!activeLiveEvent) {
        const live = evts.find((e) => e.assignment_status === 'in_progress') ||
                     evts.find((e) => e.assignment_status === 'accepted');
        if (live) setActiveLiveEvent(live);
      } else {
        const refreshed = evts.find((e) => e.id === activeLiveEvent.id);
        if (refreshed) setActiveLiveEvent(refreshed);
      }
    } catch (err) {
      console.error('[OrganizerLayout] loadEvents failed:', err);
    } finally {
      setLoading(false);
    }
  }, [activeLiveEvent]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Socket.io Real-Time Listeners
  useEffect(() => {
    if (!socket) return;

    const handleNewAssignment = (data) => {
      showToast(`🔔 New Event Assigned: ${data.eventDate} at ${data.venueName || 'Venue'}!`, 'warning');
      loadEvents();
    };

    const handleStatusUpdate = () => {
      loadEvents();
    };

    socket.on('event_assigned', handleNewAssignment);
    socket.on('status_update', handleStatusUpdate);

    return () => {
      socket.off('event_assigned', handleNewAssignment);
      socket.off('status_update', handleStatusUpdate);
    };
  }, [socket, loadEvents]);

  const handleRespond = async (eventId, action, reason = '') => {
    try {
      await axios.post(
        `${API}/api/organizer/events/${eventId}/respond`,
        { action, reason },
        { withCredentials: true }
      );
      showToast(`Event assignment ${action === 'accept' ? 'accepted' : 'declined'} successfully`, 'success');
      loadEvents();
    } catch (err) {
      showToast(err.response?.data?.error || 'Action failed', 'error');
    }
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await axios.post(
        `${API}/api/organizer/events/${eventId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      showToast(`Event status updated to ${newStatus}`, 'success');
      loadEvents();
    } catch (err) {
      showToast(err.response?.data?.error || 'Status update failed', 'error');
    }
  };

  const handleEventCompleted = (updatedBooking) => {
    showToast(`🏁 Event #${updatedBooking.id} marked as completed! Good job!`, 'success');
    loadEvents();
    if (activeLiveEvent?.id === updatedBooking.id) {
      setActiveLiveEvent(null);
    }
  };

  const pendingRequestsCount = events.filter((e) => e.assignment_status === 'assigned').length;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0B0F1F',
        color: '#fff',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 99999,
            background:
              toast.type === 'error'
                ? '#E34364'
                : toast.type === 'success'
                ? '#27C85A'
                : toast.type === 'warning'
                ? '#f5a623'
                : '#9F5CFF',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            fontSize: 13,
            fontWeight: 700,
            animation: 'slideInRight 0.25s ease-out',
          }}
        >
          {toast.message}
        </div>
      )}

      {/* Modern Top Header / Navbar */}
      <header
        style={{
          background: 'rgba(16, 20, 40, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          padding: '12px 20px',
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          {/* Logo & Portal Badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={logo} alt="PixlyBooth" style={{ height: 32 }} />
            <div style={{ height: 20, width: 1, background: 'rgba(255,255,255,0.15)' }} />
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#00D2FF',
                background: 'rgba(0, 210, 255, 0.12)',
                border: '1px solid rgba(0, 210, 255, 0.3)',
                padding: '3px 10px',
                borderRadius: 20,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Event Organizer Portal
            </span>
          </div>

          {/* User Profile & Sign Out */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Live Socket Indicator */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                color: '#27C85A',
                background: 'rgba(39, 200, 90, 0.1)',
                padding: '4px 10px',
                borderRadius: 20,
              }}
              title="Real-time WebSocket connected"
            >
              <div
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: '#27C85A',
                  boxShadow: '0 0 8px #27C85A',
                }}
              />
              <span style={{ fontWeight: 600 }}>Live Sync</span>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{user?.name}</div>
              <div style={{ fontSize: 10, color: '#7c7c9c' }}>{user?.email}</div>
            </div>

            <button
              onClick={() => logout()}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 8,
                padding: '6px 12px',
                color: '#a0a0b8',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Mobile-Friendly Navigation Tabs */}
        <div style={{ maxWidth: 1100, margin: '10px auto 0' }}>
          <nav
            style={{
              display: 'flex',
              gap: 8,
              borderTop: '1px solid rgba(255,255,255,0.05)',
              paddingTop: 8,
              overflowX: 'auto',
              scrollbarWidth: 'none',
            }}
          >
            {[
              { id: 'assignments', label: '📋 My Assignments', badge: pendingRequestsCount },
              { id: 'live', label: '⚡ Live Event & Checklist' },
              { id: 'history', label: '📜 Event History' },
              { id: 'profile', label: '👤 Host Profile' },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: isActive ? 'rgba(159, 92, 255, 0.2)' : 'transparent',
                    color: isActive ? '#fff' : '#a0a0b8',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    borderBottom: isActive ? '2px solid #9F5CFF' : '2px solid transparent',
                  }}
                >
                  <span>{tab.label}</span>
                  {tab.badge > 0 && (
                    <span
                      style={{
                        background: '#f5a623',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 800,
                        padding: '1px 6px',
                        borderRadius: 10,
                      }}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Main View Container */}
      <main style={{ flex: 1, maxWidth: 1100, width: '100%', margin: '0 auto', padding: '24px 16px' }}>
        {activeTab === 'assignments' && (
          <AssignmentsView
            events={events}
            loading={loading}
            currentTab={assignmentSubTab}
            setCurrentTab={setAssignmentSubTab}
            onRespond={handleRespond}
            onSelectLiveEvent={(event) => {
              setActiveLiveEvent(event);
              setActiveTab('live');
            }}
            onOpenCompleteModal={(event) => setCompletionModalEvent(event)}
          />
        )}

        {activeTab === 'live' && (
          <LiveEventView
            event={activeLiveEvent}
            allEvents={events}
            onSelectEvent={(evt) => setActiveLiveEvent(evt)}
            onStatusChange={handleStatusChange}
            onOpenCompleteModal={(event) => setCompletionModalEvent(event)}
          />
        )}

        {activeTab === 'history' && <HistoryView events={events} />}

        {activeTab === 'profile' && <ProfileView />}
      </main>

      {/* Event Completion Modal */}
      {completionModalEvent && (
        <EventCompletionModal
          event={completionModalEvent}
          onClose={() => setCompletionModalEvent(null)}
          onCompleted={handleEventCompleted}
        />
      )}
    </div>
  );
}
